"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/animate-ui/primitives/radix/dropdown-menu";
import { invalidatePublishedLinksQuery } from "@/hooks/queries/usePublishedLinksQuery";
import { usePublishLimitGate } from "@/hooks/usePublishLimitGate";
import { adjustPlanUsagePublishedCount } from "@/lib/client/plan-usage-cache";
import { syncScrollFieldsToQueryCache } from "@/lib/client/scroll-query-cache-bridge";
import { useNotebook } from "@/lib/notebook/core/store";
import { analytics } from "@/lib/analytics/analytics";
import { AnalyticsEvents } from "@/lib/analytics/events";
import { publicNotebookUrl } from "@/lib/notebook/public/public-url";
import { useQueryClient } from "@tanstack/react-query";
import "@/lib/notebook/chrome/toolbar/nb-publish-menu.css";

export function NotebookPublishMenu() {
  const qc = useQueryClient();
  const notebookId = useNotebook((s) => s.notebookId);
  const notebook = useNotebook((s) => s.notebook);
  const locked = useNotebook((s) => s.locked);
  const planReadOnly = useNotebook((s) => s.planReadOnly);
  const setPublishState = useNotebook((s) => s.setPublishState);
  const { guardPublish, handlePublishLimitError, proLimitDialog } =
    usePublishLimitGate();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const isPublished = Boolean(notebook?.is_published);
  const publicShortId = notebook?.public_short_id ?? null;
  const publicSlug = notebook?.public_slug ?? null;

  const publicUrl = useMemo(() => {
    if (typeof window === "undefined") return null;
    return publicNotebookUrl(
      publicShortId,
      publicSlug,
      window.location.origin,
    );
  }, [publicShortId, publicSlug]);

  const updatePublish = async (nextPublished: boolean) => {
    if (!notebookId || busy) return;

    if (nextPublished) {
      if (
        !guardPublish(isPublished, {
          locked: Boolean(locked && planReadOnly),
        })
      ) {
        return;
      }
    } else if (locked && planReadOnly) {
      // Unpublish stays available on read-only notes.
    }

    setBusy(true);
    try {
      const res = await fetch(`/api/notebook/${notebookId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: notebookId,
          is_published: nextPublished,
        }),
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err = new Error(json?.error ?? "Publish failed") as Error & {
          code?: string;
        };
        err.code = json?.code;
        throw err;
      }
      const json = await res.json();
      const next = json?.success ? json.data : json;
      setPublishState(
        Boolean(next.is_published),
        next.public_slug ?? null,
        next.public_short_id ?? null,
      );
      syncScrollFieldsToQueryCache(notebookId, {
        is_published: Boolean(next.is_published),
      });
      invalidatePublishedLinksQuery(qc);
      if (next.is_published) {
        if (!isPublished) {
          adjustPlanUsagePublishedCount(qc, 1);
        }
        analytics.capture(AnalyticsEvents.notebookPublished, {
          notebook_id: notebookId,
        });
        toast.success("Notebook is live", {
          description: "Tap Published to copy your share link.",
          duration: 5000,
        });
      } else {
        if (isPublished) {
          adjustPlanUsagePublishedCount(qc, -1);
        }
        toast.success("Notebook unpublished");
      }
    } catch (err) {
      if (handlePublishLimitError(err)) return;
      toast.error(
        err instanceof Error ? err.message : "Could not update publish state",
      );
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Public link copied");
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (!notebookId) return null;

  if (!isPublished) {
    return (
      <>
        <div className="nb-publish-menu-root">
          <button
            type="button"
            disabled={busy}
            className="nb-publish-menu__trigger"
            aria-label="Publish notebook"
            onClick={() => void updatePublish(true)}
          >
            {busy ? "Publishing…" : "Publish"}
          </button>
        </div>
        {proLimitDialog}
      </>
    );
  }

  return (
    <>
      <div className="nb-publish-menu-root">
        <DropdownMenu modal={!locked}>
          <DropdownMenuTrigger asChild disabled={busy}>
            <button
              type="button"
              disabled={busy}
              className="nb-publish-menu__trigger is-live"
              aria-label="Published — manage link"
              title={locked ? "Preserved on your current plan" : undefined}
            >
              Published
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="nb-publish-menu"
          >
            <DropdownMenuItem
              onClick={() => void copyLink()}
              className="nb-publish-menu__item"
              disabled={!publicUrl}
            >
              {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
              {copied ? "Copied" : "Copy link"}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="nb-publish-menu__sep" />
            <DropdownMenuItem
              onClick={() => void updatePublish(false)}
              className="nb-publish-menu__item nb-publish-menu__item--danger"
            >
              <Image
                src="/world-globe.png"
                alt=""
                width={15}
                height={15}
                className="nb-publish-menu__item-icon"
                draggable={false}
                unoptimized
                aria-hidden
              />
              Unpublish
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {proLimitDialog}
    </>
  );
}
